import { useEffect, useState } from "react";
import { FoundingBadge } from "@/components/FoundingBadge";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, BookmarkCheck, BookOpen, CalendarDays, CreditCard, HeartHandshake, Image as ImageIcon, Inbox, LayoutDashboard, Library, LogOut, Megaphone, MessageCircleQuestion, Settings2, Link2, Sprout, Tag, TrendingUp, Users, Video } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";
import { api } from "@/lib/api";

const MEMBER_LINKS = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/app/ask", label: "Ask Lindsay", icon: MessageCircleQuestion, id: "ask" },
  { to: "/app/foundations", label: "My Roots Profile", icon: Sprout, id: "foundations", also: ["/app/brand-foundations"] },
  { to: "/app/guides", label: "Your Guide Book", icon: Library, id: "guides" },
  { to: "/app/playbook", label: "My playbook", icon: BookmarkCheck, id: "playbook" },
  { to: "/app/drops", label: "Seasonal & drops", icon: Megaphone, id: "drops" },
  { to: "/app/calls", label: "Pro group calls", icon: Video, id: "calls" },
  { to: "/app/survey", label: "Give feedback", icon: HeartHandshake, id: "survey" },
  { to: "/app/membership", label: "Membership", icon: CreditCard, id: "membership" },
];

const ADMIN_LINKS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, id: "overview" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
  { to: "/admin/queue", label: "Escalation queue", icon: Inbox, id: "queue" },
  { to: "/admin/kb", label: "Knowledge bank", icon: BookOpen, id: "kb" },
  { to: "/admin/reports", label: "Answer reports", icon: TrendingUp, id: "reports" },
  { to: "/admin/resources", label: "Templates & videos", icon: Library, id: "resources" },
  { to: "/admin/members", label: "Members & settings", icon: Users, id: "members" },
  { to: "/admin/invites", label: "Trial groups & links", icon: Link2, id: "invites" },
  { to: "/admin/discount-codes", label: "Discount codes", icon: Tag, id: "discount-codes" },
  { to: "/admin/content", label: "Seasonal & drops", icon: Megaphone, id: "content" },
  { to: "/admin/calls", label: "Pro group calls", icon: Video, id: "calls" },
  { to: "/admin/reviews", label: "Reviews & testimonials", icon: HeartHandshake, id: "reviews" },
  { to: "/admin/announcements", label: "Announcements & sessions", icon: CalendarDays, id: "announcements" },
  { to: "/admin/surveys", label: "Monthly surveys", icon: BarChart3, id: "surveys" },
  { to: "/admin/site", label: "Site, photos & alerts", icon: ImageIcon, id: "site" },
];

const BetaCountdown = ({ access }) => {
  if (!access?.beta_mode || !access.beta_end_date || ["active", "trialing"].includes(access.subscription_status)) return null;
  const days = Math.ceil((new Date(`${access.beta_end_date}T23:59:59`) - new Date()) / 86400000);
  if (days < 0) return null;
  return (
    <div className="mb-8 rounded-2xl bg-navy text-white px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="beta-countdown">
      <p className="text-sm">
        <span className="font-serif text-2xl text-sun mr-2" data-testid="beta-countdown-days">{days}</span>
        {days === 1 ? "day" : "days"} left of the free beta. Founding members get 20% off for life with code <span className="font-mono bg-white/15 px-2 py-0.5 rounded">{access.founding_code}</span>
      </p>
      <NavLink to="/app/membership" data-testid="beta-countdown-cta" className="rounded-full bg-pinkb hover:bg-pinkb/90 px-4 py-1.5 text-sm transition-colors">See membership</NavLink>
    </div>
  );
};

export default function AppShell({ admin = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = admin ? ADMIN_LINKS : MEMBER_LINKS;
  const location = useLocation();
  const [access, setAccess] = useState(null);
  useEffect(() => {
    if (!admin) api.get("/me/access").then((r) => setAccess(r.data)).catch(() => {});
  }, [admin, location.pathname]);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!admin) return undefined;
    const load = () => api.get("/admin/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    window.addEventListener("admin-notifications-read", load);
    return () => window.removeEventListener("admin-notifications-read", load);
  }, [admin, location.pathname]);
  const locked = !admin && access && !access.has_access && !["/app/membership", "/app/survey"].includes(location.pathname);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="relative z-10 min-h-screen flex flex-col md:flex-row">
      <aside className="min-w-0 w-full md:w-64 md:min-h-screen md:sticky md:top-0 border-b md:border-b-0 md:border-r border-white/10 bg-navy text-white flex md:flex-col">
        <div className="hidden md:block px-6 py-7" data-testid="shell-brand">
          <img src={BRAND.logo} alt="Roots Marketing Co" className="h-14 w-auto" />
          <div className="font-serif text-lg leading-none mt-4">{BRAND.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/60 mt-1.5">{admin ? "Lindsay's admin" : ({ cpg: "Food & drink founders", hospitality: "Independent hospitality" })[user?.audience] || "Food, drink & hospitality"}</div>
        </div>
        <nav className="flex md:flex-col gap-1 px-2 md:px-3 py-2 md:py-0 overflow-x-auto flex-1 min-w-0">
          {links.map(({ to, label, icon: Icon, end, id, also }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors duration-200 ${
                  isActive || also?.includes(location.pathname) ? "bg-pinkb text-white" : "text-white/70 hover:text-white hover:bg-white/10"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">{label}</span>
              {id === "reports" && unread > 0 && <span data-testid="nav-reports-badge" className="ml-auto rounded-full bg-sun text-navy text-[10px] font-mono px-1.5 py-0.5">{unread}</span>}
            </NavLink>
          ))}
          {user?.role === "admin" && !admin && (
            <NavLink to="/admin" data-testid="nav-to-admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:text-white">
              <Settings2 className="h-4 w-4" /><span className="hidden sm:inline">Admin</span>
            </NavLink>
          )}
          {admin && (
            <NavLink to="/app" data-testid="nav-to-member" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:text-white">
              <Users className="h-4 w-4" /><span className="hidden sm:inline">Member view</span>
            </NavLink>
          )}
        </nav>
        <div className="md:p-4 p-2 md:border-t border-white/10 flex items-center md:block">
          <div className="hidden md:block mb-3">
            <div className="text-sm truncate" data-testid="shell-user-name">{user?.name}</div>
            <div className="text-xs text-white/60 truncate">{user?.business_name || user?.email}</div>
            <FoundingBadge dark className="mt-2" testId="shell-founding-badge" />
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 text-sm text-white/70 hover:text-sun transition-colors px-3 py-2">
            <LogOut className="h-4 w-4" /><span className="hidden md:inline">Log out</span>
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 px-5 sm:px-8 lg:px-14 py-8 lg:py-12">
        {!admin && <BetaCountdown access={access} />}
        {locked ? <Navigate to="/app/membership" replace /> : <Outlet />}
      </main>
    </div>
  );
}
